import { datadogRum } from '@datadog/browser-rum';
import { createRoot } from 'react-dom/client';
import DsModal from '@/common/DsModal';
import PendingSpinner from '@/common/PendingSpinner';

export type MfeModalPlaceholderCloseReason =
  | 'cancelled'
  | 'mfe-opened'
  | 'timed-out'
  | 'user-dismissed';

type Options = {
  datadogContext?: Record<string, string>;
  message: string;
  onDismiss: () => void;
};

// Shows a loading modal in its own React root so any caller can use it without mounting a host.
export default function showMfeModalPlaceholder({
  datadogContext,
  message,
  onDismiss
}: Options) {
  const container = document.createElement('div');
  const root = createRoot(container);
  const shownAt = Date.now();
  let isShown = true;

  const hide = (reason: MfeModalPlaceholderCloseReason) => {
    if (!isShown) return;

    isShown = false;
    datadogRum.addAction('mfe-modal-placeholder-closed', {
      ...datadogContext,
      closeReason: reason,
      shownForMs: Date.now() - shownAt
    });
    root.unmount();
    container.remove();
  };

  document.body.appendChild(container);
  datadogRum.addAction('mfe-modal-placeholder-shown', datadogContext);
  root.render(
    <DsModal
      closeIcon
      desktopWidth="320px"
      mobileStyle="inset"
      isOpen
      onClose={() => {
        hide('user-dismissed');
        onDismiss();
      }}
    >
      <div
        aria-live="polite"
        className="flex min-h-[200px] flex-col items-center justify-center gap-16 px-8 py-24"
        data-testid="mfe-modal-placeholder"
        role="status"
      >
        <p className="text-center text-base font-bold">{message}</p>
        <PendingSpinner className="[&>svg]:h-64 [&>svg]:w-64" size="lg" />
      </div>
    </DsModal>
  );

  return hide;
}
